import React from 'react'
import clsx from 'clsx'
import HeaderHelpSection from '@src/routes/Configurators/components/HeaderHelpSection'
import LeftSideBar from '@src/routes/Configurators/components/LeftSideBar'
import Container from '@material-ui/core/Container'
import { makeStyles } from '@material-ui/core/styles'
import { Theme, Grid, Box, Typography } from '@material-ui/core'
import { SidebarItem } from '@src/routes/Configurators/components/LeftNavigation/LeftNavigation'
import Breadcrumb from '@src/components/Breadcrumb'
import DesktopRequired from '../DesktopRequired'
import useZoomAwareBreakpoints from '@src/utils/zoomAwareBreakpoints'

const useStyles = makeStyles((theme: Theme) => {
  return {
    mainContainer: {
      padding: theme.spacing(2, 20, 0, 24),
    },
    tabletAndMobileContainer: {
      padding: theme.spacing(0),
    },
    container: {
      padding: theme.spacing(0, 0, 0, 10),
    },
    content: {
      border: `1px solid ${theme.palette.grey[400]}`,
      padding: theme.spacing(6, 6, 6, 6),
    },
    tabContent: {
      border: `1px solid ${theme.palette.grey[400]}`,
      padding: theme.spacing(3, 6, 6, 6),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: theme.spacing(6, 0, 10, 0),
    },
    nextStepButton: {
      justifyContent: 'flex-end',
      margin: theme.spacing(6, 0, 0, 4),
    },
    previousStepButton: {
      margin: theme.spacing(6, 0, 0, 0),
    },
    disableRootStyle: {
      padding: 0,
      border: 'none',
    },
    buttonsContainerRightAlign: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    buttonsContainerSpaceBetween: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  }
})

interface BreadcrumbItem {
  url?: string
  label: React.ReactNode
}

interface ConfiguratorLayoutProps {
  title: React.ReactNode
  breadcrumbItems: BreadcrumbItem[]
  bodyContent: React.ReactNode
  sidebarItems: SidebarItem[]
  nextStepButton?: React.ReactElement
  previousStepButton?: React.ReactElement
  disableRootStyle?: boolean
  bottomDisclaimer?: React.ReactElement
  tabPadding?: boolean
  sidebarBottom?: React.ReactElement
  spaceOutButtons?: boolean
}

const ConfiguratorLayout: React.FC<ConfiguratorLayoutProps> = ({
  title,
  breadcrumbItems,
  bodyContent,
  sidebarItems,
  nextStepButton,
  previousStepButton: previousStepButton,
  disableRootStyle,
  bottomDisclaimer,
  tabPadding,
  sidebarBottom,
  spaceOutButtons,
}) => {
  const classes = useStyles()
  const isDesktop = useZoomAwareBreakpoints(960, true)

  return (
    <Container
      className={
        isDesktop ? classes.mainContainer : classes.tabletAndMobileContainer
      }
      maxWidth={false}
    >
      <Breadcrumb items={breadcrumbItems} />
      {isDesktop ? (
        <Box className={classes.header}>
          <Typography variant="h1" component="h1">
            {title}
          </Typography>
          <HeaderHelpSection />
        </Box>
      ) : null}

      {isDesktop ? (
        <Grid container>
          <Grid item xs={2}>
            <LeftSideBar sidebarItems={sidebarItems} />
            {sidebarBottom ? <>{sidebarBottom}</> : null}
          </Grid>
          <Grid item xs={10} className={classes.container}>
            <Box
              className={clsx(
                tabPadding ? classes.tabContent : classes.content,
                {
                  [classes.disableRootStyle]: disableRootStyle,
                }
              )}
            >
              {bodyContent}
            </Box>
            <Box
              className={
                spaceOutButtons
                  ? classes.buttonsContainerSpaceBetween
                  : classes.buttonsContainerRightAlign
              }
            >
              {previousStepButton ? (
                <div className={classes.previousStepButton}>
                  {previousStepButton}
                </div>
              ) : null}
              {nextStepButton ? (
                <div className={classes.nextStepButton}>{nextStepButton}</div>
              ) : null}
            </Box>
            {bottomDisclaimer && <Box>{bottomDisclaimer}</Box>}
          </Grid>
        </Grid>
      ) : (
        <DesktopRequired />
      )}
    </Container>
  )
}

export default ConfiguratorLayout
